import { useRef } from 'react';

// Taken from https://github.com/facebook/react/issues/14490#issuecomment-486892561

export type HookRef<Value> = {
  readonly current: Value;
};

const initialEmptyValue = Symbol('useStableInstance.initialEmptyValue');

/**
 * Used to create a value that should only be initialized on mount and have a stable instance through the lifetime of the hook.
 * This should only be used for non-stateful values that need to have a singleton instance
 * that needs to have a stable reference through the lifetime of the component.
 * If you need a stateful reference, you should use useMemo/useCallback to ensure that you have stable
 * and up to date references to your value's dependencies.
 *
 * BAD example: The sendRpc function may reference a stale value of counter.
 * ```typescript
 * const [counter, setCounter] = useState(0);
 * const sendRpc = useStableInstance(() => () => sendRpc(counter)));
 * ```
 * GOOD example: The initial value doesn't depend on any stateful value and is only created once.
 * ```typescript
 * const debouncedSendRpc = useStableInstance(() => debounce((...args) => sendRpc(...args), 1000));
 * ```
 */
const useStableInstance = <TValue>(getInitialValue: () => TValue): TValue => {
  const initializedValueRef = useRef<TValue | typeof initialEmptyValue>(initialEmptyValue);

  if (initializedValueRef.current === initialEmptyValue) {
    initializedValueRef.current = getInitialValue();
  }

  return initializedValueRef.current;
};

export default useStableInstance;
